import { __decorate, __metadata } from 'tslib';
import { ProviderRegistry, InjectorService, DynamicLoaderRegistry } from '@tencent/ioc';
import { nameOf } from '@tencent/workbench-core';
import { scheduler, Priority } from '@tencent/idle-loader';
import { Service } from '../../decorators/class/service.js';
import { WorkbenchSettingsService } from '../settings/setting-service.js';
import { FeatureRegistry } from '../../registries/feature-registry.js';
import { $logger } from '../../controllers/logger/logger.js';
import { AdapterMatchService } from '../../controllers/adapter/adapter-match-service.js';
import { ReplaySubject } from 'rxjs';
import { StateManager } from '../store/state-manager.js';
import { WorkbenchMountSettings } from '../settings/auto-sync-settings.js';
import { FeatureFlagService } from './feature-flag-service.js';

/**
 * Feature 服务，用于加载 Feature
 *
 * @public
 */
let FeatureService = class FeatureService {
    constructor(settings, adapterMatcher, store, featureFlagService) {
        this.settings = settings;
        this.adapterMatcher = adapterMatcher;
        this.store = store;
        this.featureFlagService = featureFlagService;
        this.featuresSettingChange = new ReplaySubject();
        this.store.register(WorkbenchMountSettings.Features, []).subscribe(this.featuresSettingChange);
    }
    isFeatureProvider(provider) {
        return (provider === null || provider === void 0 ? void 0 : provider.type) === 'feature';
    }
    /**
     * 监听增量feature变化
     * @internal
     */
    subscribeToFeatureChange() {
        this.featuresSettingChange.subscribe((features) => {
            features.filter(token => !!token && ProviderRegistry.has(token)).forEach((token) => {
                const provider = ProviderRegistry.get(token);
                if (!provider) {
                    throw new ReferenceError(`Feature ${nameOf(token)} is not loaded, make sure you have imported the controller code somewhere.`);
                }
                if (!this.isFeatureProvider(provider)) {
                    throw new TypeError(`Provider ${nameOf(token)} is not a feature.`);
                }
                this.buildFeature(provider);
            });
        });
    }
    mount(targets, options) {
        targets.forEach((target, i) => {
            var _a;
            if (!target) {
                $logger.error(`The ${i}-index feature is undefined`);
                return;
            }
            const provider = ProviderRegistry.get(target);
            if (this.isFeatureProvider(provider)) {
                this.buildFeature(provider, (_a = options === null || options === void 0 ? void 0 : options.idle) !== null && _a !== void 0 ? _a : this.settings.idle);
            }
        });
    }
    buildFeature(provider, idle = false) {
        var _a;
        if (!this.featureFlagService.getFlag(`feature.${(_a = provider.name) !== null && _a !== void 0 ? _a : ''}`)) {
            return;
        }
        if (idle) {
            scheduler.scheduleTask(() => {
                this.mountFeature(provider);
            }, { priority: Priority.normal });
        }
        else {
            this.mountFeature(provider);
        }
    }
    mountFeature(provider) {
        if (!provider.instance) {
            provider.instance = InjectorService.get(provider.useClass);
        }
        const featureProvider = FeatureRegistry.get(provider.useClass);
        if (!(featureProvider === null || featureProvider === void 0 ? void 0 : featureProvider.mounted)) {
            this.adapterMatcher.matchOpenedEditors(featureProvider).forEach((editor) => {
                featureProvider.onOpen();
                InjectorService.emitAt('$onEditorOpen', provider.useClass, editor);
            });
            featureProvider.mounted = true;
            const loader = DynamicLoaderRegistry.get(provider.provide);
            if (loader) {
                loader.isReady = true;
            }
        }
    }
};
FeatureService = __decorate([
    Service({ overridable: true }),
    __metadata("design:paramtypes", [WorkbenchSettingsService,
        AdapterMatchService,
        StateManager,
        FeatureFlagService])
], FeatureService);

export { FeatureService };
//# sourceMappingURL=feature-service.js.map
